import axios from 'axios';

const apiUrl = process.env.VUE_APP_API_URL || 'http://localhost:8000';

export const api = axios.create({
  baseURL: apiUrl,
  withCredentials: true, // Important: send cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Send tokens to backend to establish session cookies
 */
export async function createSession(accessToken: string, refreshToken: string) {
  const response = await api.post('/auth/session', {
    access_token: accessToken,
    refresh_token: refreshToken,
  });
  return response;
}

/**
 * Hand the session to the application without putting it in a URL.
 *
 * The two apps are different origins, so a localStorage session cannot cross between
 * them and the tokens used to travel in the redirect query string — where access logs,
 * browser history, Referer headers, analytics and proxies all see them. Cleaning the URL
 * afterwards is too late; everything in that list has already recorded it.
 *
 * The backend stores the session against an opaque one-time code that expires in a
 * minute, and only the code travels. Returns null when the handoff is unavailable, so
 * the caller can decide rather than stranding the user mid-login.
 */
export async function createHandoffCode(
  accessToken: string,
  refreshToken: string
): Promise<string | null> {
  try {
    const { data } = await api.post(
      '/auth/handoff',
      { refresh_token: refreshToken },
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );
    return data?.handoff_code ?? null;
  } catch {
    return null;
  }
}

/**
 * The address to send the browser to after signing in.
 *
 * Uses the one-time code when the backend issued one. If it did not, the caller is left
 * to handle it: silently falling back to tokens in the URL would reintroduce the leak
 * exactly when something is already wrong.
 */
export function buildHandoffUrl(
  mainAppUrl: string,
  handoffCode: string,
  redirect: string
): string {
  const target = new URL(`${mainAppUrl}/auth/callback`);
  target.searchParams.set('handoff_code', handoffCode);
  target.searchParams.set('redirect', redirect);
  return target.toString();
}

export default api;

